"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Renamed to BlogClient for use with the new layout pattern

interface Post {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    published_at: string;
    image: string;
    collectionId: string;
    collectionName: string;
    author: string;
    expand?: {
        author?: {
            name: string;
            avatar: string;
        };
    };
}

export default function BlogClient() {
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const { blogApi } = await import('@/lib/api-client');
                const result = await blogApi.getPosts(1, 20);

                if (result.success && result.posts) {
                    setPosts(result.posts);
                }
            } catch (error) {
                console.error("Error fetching posts:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    const getImageUrl = (post: Post) => {
        if (!post.image) return null;
        return `${process.env.NEXT_PUBLIC_POCKETBASE_URL}/api/files/${post.collectionId}/${post.id}/${post.image}`;
    };

    return (
        <div className="min-h-screen bg-brand-navy flex flex-col">
            <Header />

            <main className="flex-1 container mx-auto px-6 py-12">
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-4">
                        Latest <span className="text-brand-orange">Insights</span>
                    </h1>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Stay updated with the latest market analysis, trading strategies, and platform updates from the 0DTE Toolbox team.
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-orange"></div>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-20 bg-white/5 rounded-lg border border-gray-700">
                        <p className="text-gray-400 text-xl">No posts found yet. Check back soon!</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {posts.map((post) => (
                            <Link
                                key={post.id}
                                href={`/blog/${post.slug}`}
                                className="group bg-white/5 border border-gray-700 rounded-lg overflow-hidden hover:border-brand-blue/50 transition-all hover:-translate-y-1"
                            >
                                {/* Image Placeholder or Actual Image */}
                                <div className="h-48 bg-gray-800 relative overflow-hidden">
                                    {post.image ? (
                                        <img
                                            src={getImageUrl(post) || ""}
                                            alt={post.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-brand-blue/10 text-brand-blue/30">
                                            <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                <div className="p-6">
                                    <div className="flex items-center gap-2 text-xs text-brand-orange mb-3">
                                        <span>{new Date(post.published_at).toLocaleDateString()}</span>
                                        <span>•</span>
                                        <span>{post.expand?.author?.name || "0DTE Team"}</span>
                                    </div>

                                    <h2 className="text-xl font-bold text-white mb-3 group-hover:text-brand-blue transition-colors line-clamp-2">
                                        {post.title}
                                    </h2>

                                    <p className="text-gray-400 text-sm line-clamp-3 mb-4">
                                        {post.excerpt}
                                    </p>

                                    <span className="text-brand-blue text-sm font-semibold group-hover:underline">
                                        Read Article →
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}
